import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import {
  DataGrid,
  gridClasses,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  gridPaginationModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import MedicationIcon from "@mui/icons-material/Medication";
//import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { red, green, orange } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../navigation/routeConfig";
import { useQuery } from "@apollo/client";
import { SELL_LIST_PAGINATED } from "../../queries/product/productTypeDefs";
import { UserContext } from "../../hooks/userContext";
import { Badge, CircularProgress, TextField, debounce, IconButton } from "@mui/material";
import moment from "moment";
//import {useMutationDeleteProduct} from "../../queries/product/hooks/useMutationDeleteProduct";
//import {NotificationType} from "../../constants/global";
//import {ToasterContext} from "../../components/shared/Toaster/ToasterContext";
//import Dialog from '@mui/material/Dialog';
//import DialogActions from '@mui/material/DialogActions';
//import DialogContent from '@mui/material/DialogContent';
//import DialogContentText from '@mui/material/DialogContentText';
//import DialogTitle from '@mui/material/DialogTitle';
//import {MY_SALES, POSTINGS} from "../../queries/product/productTypeDefs";
import {
  formatPrice,
  //removeObjectWithId
} from "../../components/utils/utils";
import axios from "axios";

interface ISellListProps {
  isAdmin?: boolean;
}

const SellGridPagination = (props: { rowCount?: number }) => {
  const apiRef = useGridApiContext();
  const theme = useTheme();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const totalPages = Math.max(Math.ceil((props.rowCount ?? 0) / (paginationModel.pageSize || 10)), 1);
  const currentPage = paginationModel.page + 1;
  const windowStart = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
  const windowEnd = Math.min(totalPages, windowStart + 2);
  const visiblePages = Array.from({ length: windowEnd - windowStart + 1 }, (_, index) => windowStart + index);
  const shouldShowLastPage = totalPages > 3 && windowEnd < totalPages;
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        px: 2,
        py: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Page {currentPage} of {totalPages}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton size="small" onClick={() => apiRef.current.setPage(0)} disabled={isFirstPage}>
          <FirstPageRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => apiRef.current.setPage(currentPage - 2)} disabled={isFirstPage}>
          <KeyboardArrowLeftRoundedIcon fontSize="small" />
        </IconButton>

        {visiblePages.map((page) => {
          const isSelected = currentPage === page;
          return (
            <Button
              key={page}
              size="small"
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(page - 1)}
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                borderRadius: "8px",
                fontWeight: 600,
                p: 0,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderColor: alpha(theme.palette.primary.main, 0.35),
                ...(isSelected && {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }),
              }}
            >
              {page}
            </Button>
          );
        })}
        {shouldShowLastPage && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
              ...
            </Typography>
            <Button
              size="small"
              variant={currentPage === totalPages ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(totalPages - 1)}
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                borderRadius: "8px",
                fontWeight: 600,
                p: 0,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderColor: alpha(theme.palette.primary.main, 0.35),
                ...(currentPage === totalPages && {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }),
              }}
            >
              {totalPages}
            </Button>
          </>
        )}

        <IconButton size="small" onClick={() => apiRef.current.setPage(currentPage)} disabled={isLastPage}>
          <KeyboardArrowRightRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => apiRef.current.setPage(totalPages - 1)} disabled={isLastPage}>
          <LastPageRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const getColor: any = () => {
  return {
    Active: {
      color: green[700],
      bgColor: green[200],
    },
    Inactive: {
      color: red[700],
      bgColor: red[200],
    },
    Sold: {
      color: orange[700],
      bgColor: orange[200],
    },
  };
};

export const SellList: React.FunctionComponent<ISellListProps> = ({ isAdmin }) => {
  const { id }: any = useContext(UserContext);
  //  const {addToast} = useContext(ToasterContext);
  const navigate = useNavigate();
  //  const [open, setOpen] = useState(false);
  //  const [productId, setProductId] = useState<number>(0);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [currentWhere, setCurrentWhere] = useState<any>(() =>
    isAdmin ? {} : { User: { is: { id: { equals: +id } } } }
  );

  useEffect(() => {
    setCurrentWhere(isAdmin ? {} : { User: { is: { id: { equals: +id } } } });
    setPage(0);
  }, [isAdmin, id]);

  const { data, loading: listLoading } = useQuery(SELL_LIST_PAGINATED, {
    variables: {
      where: currentWhere,
      orderBy: [{ createdAt: "desc" }],
      skip: page * pageSize,
      take: pageSize,
    },
    skip: !isAdmin && (id == null || id === undefined || Number.isNaN(+id)),
    fetchPolicy: "no-cache",
    pollInterval: 5000,
  });

  const paginatedResult = data?.findManyProductPaginated;
  const lastKnownCount = useRef(0);
  if (paginatedResult?.totalCount != null) {
    lastKnownCount.current = paginatedResult.totalCount;
  }
  const totalRows = lastKnownCount.current;

  const rows = useMemo(() => {
    const products = paginatedResult?.products;
    if (!products) return [];
    return products.map((product: any) => ({
      ...product,
      expirationDate: moment(product.expDate),
      lot: product.lotNumber,
      available: product.quantity,
      packaging: product.quantityPerPack,
      type: product.type === 1 ? "Full" : "Partial",
      price: Number(product.price),
      Pharmacy: product.Pharmacy?.legalName,
      PharmacyStatus: product.Pharmacy?.PharmacyStatus?.Status?.key,
    }));
  }, [paginatedResult?.products]);
  /*
  const [deleteProduct, { loading: deleteLoading }] = useMutationDeleteProduct({
    refetchQueries: [
      {
        query: isAdmin ? POSTINGS : MY_SALES,
        variables: isAdmin ? { orderBy: [{ createdAt: "desc" }] } : { where: { User: { is: { id: { equals: +id } } } }, orderBy: [{ createdAt: 'desc' }] },
        awaitRefetchQueries: true,
        fetchPolicy: 'no-cache'
      },
    ],
  });

  const handleDelete = (id: number) => {
    deleteProduct({ variables: { where: { id: id } } }).then(() => {
      const newRows: any = removeObjectWithId(rows, id);
      setRows(newRows);
      setOpen(false);
      setProductId(0);
      addToast({ type: NotificationType.Success, title: 'Product has been successfully deleted.' });
    }).catch((error: any) => {
      setProductId(0);
      setOpen(false);
      addToast({ type: NotificationType.Error, title: error.message  });
    });
  }
*/

  const handleScrollHorizontal = () => {
    const currentScrollPos = document.querySelector(".MuiDataGrid-virtualScroller")?.scrollLeft || 0;
    const firstColumnHeader = document.querySelector<HTMLElement>(".stickyCell");
    if (firstColumnHeader) {
      firstColumnHeader.style.transform = `translate3d(${currentScrollPos}px, 0px, 0px)`;
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    const findVirtualScroller = () => {
      const virtualScrollerElement = document.querySelector(".MuiDataGrid-virtualScroller");
      if (!virtualScrollerElement) {
        timer = setTimeout(findVirtualScroller, 100);
      } else {
        virtualScrollerElement.addEventListener("scroll", handleScrollHorizontal);
        return () => {
          virtualScrollerElement.removeEventListener("scroll", handleScrollHorizontal);
        };
      }
    };
    findVirtualScroller();
    return () => {
      timer && clearTimeout(timer);
    };
  }, []);

  const DrugCellRenderer = ({ params }: { params: GridRenderCellParams<any> }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
      const drugNdc = params?.row?.ndc;
      if (!drugNdc) return;
      const formattedNdc = drugNdc.replace(/-/g, "");
      setIsLoading(true);
      const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`;
      axios
        .get(imageUrlApi)
        .then((response) => {
          setImageUrl(response.data.imageUrl);
        })
        .catch((error) => {
          console.error("Error fetching drug image:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, []); // Only re-run if the NDC changes

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {isLoading ? (
          <Box
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              marginRight: "8px",
              bgcolor: "grey.300",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* You could add a spinner component here */}
          </Box>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt="Drug"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              marginRight: "8px",
            }}
          />
        ) : (
          <MedicationIcon
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              marginRight: "8px",
              color: "#ccc",
            }}
          />
        )}
        <span>{params.value}</span>
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "drugName",
      headerName: "Drug name",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      disableExport: true,
      disableReorder: true,
      minWidth: 300,
      cellClassName: "stickyCol",
      headerClassName: "stickyCell",
      renderCell: (params: GridRenderCellParams<any>) => {
        return <DrugCellRenderer params={params} />;
      },
    },
    {
      field: "Pharmacy",
      headerName: "Seller",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      filterable: false,
      disableExport: true,
      disableReorder: true,
      minWidth: 200,
      renderCell: (params: any) => (
        <Badge badgeContent={params.row.PharmacyStatus === "EXPIRED" ? "expired" : 0} sx={{ "& .MuiBadge-badge": { color: "#f57c00", backgroundColor: "#ffcc80", opacity: 0.8 } }}>
          {params.value}
        </Badge>
      ),
    },
    {
      field: "ndc",
      headerName: "NDC",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 150,
    },
    {
      field: "lot",
      headerName: "Lot",
      type: "number",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 90,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "expirationDate",
      headerName: "Expiration Date",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 150,
      type: "dateTime",
      valueFormatter: (params) => moment(params?.value).format("MM/YYYY"),
    },
    {
      field: "packaging",
      headerName: "Packaging",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 120,
    },
    {
      field: "available",
      headerName: "Available",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 110,
    },
    {
      field: "price",
      headerName: "Price",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 120,
      type: "number",
      align: "left",
      headerAlign: "left",
      renderCell: (params: any) => {
        return `$${formatPrice(params.value)}`;
      },
    },
    {
      field: "type",
      headerName: "Type",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 100,
    },
    {
      field: "isActive",
      headerName: "Status",
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      disableExport: true,
      disableReorder: true,
      minWidth: 100,
      renderCell: (params: any) => {
        const status = !params.value ? "Inactive" : params.row.available > 0 ? "Active" : "Sold";
        return (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "end", alignItems: "center" }}>
            <Box
              sx={{
                borderRadius: "16px",
                padding: "8px 16px",
                width: "100%",
                textAlign: "center",
                color: getColor()[status].color,
                backgroundColor: getColor()[status].bgColor,
              }}
            >
              {status}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "action",
      headerName: "",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      minWidth: 70,
      renderCell: (params: GridRenderCellParams<Date>) => (
        <Box sx={{ flex: 1, display: "flex", justifyContent: "end", alignItems: "center", pr: 2 }}>
          <EditOutlinedIcon sx={{ ml: 2, mr: 2, cursor: "pointer", color: green[500] }} onClick={() => navigate(`/sell/${params.id}`)} />
          {/*<DeleteOutlineOutlinedIcon sx={{ cursor: 'pointer', color: red[500] }} onClick={() => {
            setProductId(+params.id);
            setOpen(true);
          }} />*/}
        </Box>
      ),
    },
  ];

  const search = useCallback(
    debounce((value: string) => {
      if (!value) {
        setCurrentWhere(isAdmin ? {} : { User: { is: { id: { equals: +id } } } });
        setPage(0);
        return;
      }

      const andQuery: any = isAdmin ? [] : [{ User: { is: { id: { equals: +id } } } }];

      if (isAdmin) {
        andQuery.push({
          OR: [
            {
              drugName: {
                contains: value,
                mode: "insensitive",
              },
            },
            {
              ndc: {
                contains: value,
                mode: "insensitive",
              },
            },
            {
              Pharmacy: {
                is: {
                  legalName: {
                    contains: value,
                    mode: "insensitive",
                  },
                },
              },
            },
          ],
        });
      } else {
        andQuery.push({
          OR: [
            {
              drugName: {
                contains: value,
                mode: "insensitive",
              },
            },
            {
              ndc: {
                contains: value,
                mode: "insensitive",
              },
            },
          ],
        });
      }

      setCurrentWhere({ AND: andQuery });
      setPage(0);
    }, 1000),
    [isAdmin, id]
  );

  const handleSearch = (value: string) => {
    setSearchInput(value);
    search(value);
  };

  return (
    <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "row", pb: 3 }}>
        <Typography component="h4" variant="h6">{`${isAdmin ? "" : "My "}Postings`}</Typography>
        {!isAdmin && (
          <Button variant="contained" onClick={() => navigate(routeConfigs.newSellItem.path)}>
            Post new item
          </Button>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "row", pb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <TextField
            type="text"
            placeholder={`Search by Drug name or ${isAdmin ? "Seller" : "NDC"}...`}
            value={searchInput}
            onChange={(event: any) => handleSearch(event.target.value)}
            fullWidth
            sx={{ minHeight: "30px", minWidth: "300px" }}
            size="small"
          />
        </Box>
      </Box>
      <Box sx={{ height: "calc(100vh - 236px)", width: "100%" }}>
        <DataGrid
          aria-label="My Sales"
          loading={listLoading}
          rows={rows}
          columns={columns}
          slots={{ pagination: SellGridPagination }}
          slotProps={{ pagination: { rowCount: totalRows } as any }}
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => setPage(model.page)}
          onRowSelectionModelChange={(ids: GridRowSelectionModel) => {
            console.log(ids);
          }}
          pageSizeOptions={[10]}
          // checkboxSelection
          disableRowSelectionOnClick
          sx={{
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
              outline: "none",
            },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
              outline: "none",
            },
            "& .MuiDataGrid-footerContainer": {
              minHeight: 56,
              borderTop: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.25),
            },
          }}
          columnVisibilityModel={{
            Pharmacy: !!isAdmin,
          }}
        />
      </Box>
      {/*open && (
        <Dialog
          fullWidth
          maxWidth='sm'
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant='contained' onClick={() => handleDelete(productId)}>
              Confirm
              {deleteLoading && <CircularProgress size={14} sx={{ml: 2}} color='inherit' />}
            </Button>
          </DialogActions>
        </Dialog>
      )*/}
    </Paper>
  );
};

export default SellList;
